import React, { memo, useMemo, useState } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import { Button } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { useCurrentUser } from "store/users/selectors";
import { getCloudflareDownloadUrl } from "store/1wayteachervideo/thunks";
import { getCloudflareDownloadUnavailableMessage } from "utils/methods/cloudflareVideoDownload";
import { useAlert } from "../alertSakeBar/alertProvider";

const extractCloudflareVideoId = (url: string): string | null => {
  try {
    if (url.includes("cloudflarestream.com")) {
      const match = url.match(/cloudflarestream\.com\/([a-zA-Z0-9]+)/);
      return match ? match[1] : null;
    }
    if (url.includes(".m3u8")) {
      const match = url.match(/\/([a-zA-Z0-9]+)\/manifest\/video\.m3u8/);
      return match ? match[1] : null;
    }
    return null;
  } catch {
    return null;
  }
};

const isCloudflareUrl = (url: string | undefined): boolean =>
  !!url && (url.includes("cloudflarestream.com") || url.includes(".m3u8"));

const getWatchUrl = (iframeSrc: string): string => {
  try {
    const url = new URL(iframeSrc);
    const pathParts = url.pathname.split("/").filter((part) => part.length > 0);
    const videoId = pathParts[0];
    if (videoId && videoId.length > 10) {
      return `https://${url.hostname}/${videoId}/watch`;
    }
    return iframeSrc;
  } catch {
    return iframeSrc;
  }
};

const getPlaybackUrl = (iframeSrc: string): string => {
  try {
    if (iframeSrc.includes("cloudflarestream.com") && iframeSrc.includes("/iframe")) {
      const url = new URL(iframeSrc);
      const pathParts = url.pathname.split("/").filter((part) => part.length > 0);
      const videoId = pathParts[0];
      if (videoId) {
        return `https://${url.hostname}/${videoId}/manifest/video.m3u8`;
      }
    }
    return iframeSrc;
  } catch {
    return iframeSrc;
  }
};

type Props = {
  iframeUrl: string;
  style?: React.CSSProperties;
  is_placement?: boolean;
};

const PreviewWithPlayer: React.FC<Props> = ({
  iframeUrl,
  style = {},
  is_placement = false,
}) => {
  const playerRef = React.useRef<ReactPlayer>(null);
  const currentUser = useCurrentUser();
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const showAlert = useAlert();

  const playbackUrl = useMemo(() => getPlaybackUrl(iframeUrl), [iframeUrl]);

  const handleOpenInNewTab = () => {
    window.open(getWatchUrl(iframeUrl), "_blank", "noopener,noreferrer");
  };

  const handleDownload = async () => {
    if (!iframeUrl) return;
    setDownloading(true);
    setDownloadStatus("preparing");
    try {
      if (isCloudflareUrl(iframeUrl)) {
        const videoId = extractCloudflareVideoId(iframeUrl);
        if (videoId) {
          showAlert("Preparing download... This may take a few moments.", "info");
          let downloadUrl: string | null = null;
          let attempts = 0;
          const maxAttempts = 10;
          const pollInterval = 2000;
          while (!downloadUrl && attempts < maxAttempts) {
            try {
              const response = await getCloudflareDownloadUrl(videoId);
              const unavailableMessage = getCloudflareDownloadUnavailableMessage(response);
              if (unavailableMessage) {
                const unavailableError = new Error(unavailableMessage);
                (unavailableError as Error & { cloudflareDownloadUnavailable?: boolean }).cloudflareDownloadUnavailable = true;
                throw unavailableError;
              }
              downloadUrl = response.mp4Url || response.downloadUrl;
              if (downloadUrl && response.status === "ready") {
                setDownloadStatus("ready");
                showAlert("Download ready! Starting download...", "success");
                await new Promise((r) => setTimeout(r, 500));
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = `video-${videoId}.mp4`;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setDownloadStatus(null);
                break;
              } else if (response.status === "pending" || !downloadUrl) {
                attempts++;
                if (attempts < maxAttempts) {
                  setDownloadStatus("processing");
                  await new Promise((r) => setTimeout(r, pollInterval));
                }
              }
            } catch (error) {
              if (
                (error as Error & { cloudflareDownloadUnavailable?: boolean }).cloudflareDownloadUnavailable
              ) {
                throw error;
              }
              if (attempts >= maxAttempts - 1) throw error;
              attempts++;
              await new Promise((r) => setTimeout(r, pollInterval));
            }
          }
          if (!downloadUrl) {
            throw new Error(
              "Download is still being processed. Please try again in a few moments."
            );
          }
        }
      } else {
        setDownloadStatus("ready");
        const link = document.createElement("a");
        link.href = iframeUrl;
        link.download = "video.mp4";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloadStatus(null);
      }
    } catch (error) {
      setDownloadStatus("error");
      showAlert(
        error instanceof Error ? error.message : "Failed to prepare download. Please try again.",
        "error"
      );
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadStatus(null);
      }, 1000);
    }
  };

  const isAdmin = currentUser?.role === "owe_admin";

  const playerConfig = useMemo(() => {
    if (!is_placement) return undefined;
    return {
      file: {
        attributes: {
          disablePictureInPicture: true,
          disableRemotePlayback: true,
          controlsList: "nofullscreen" as const,
        },
      },
    };
  }, [is_placement]);
  if (!iframeUrl) return null;
  return (
    <>
      <PlayerWrapper style={style}>
        <ReactPlayer
          ref={playerRef}
          url={playbackUrl}
          width="100%"
          height="100%"
          controls
          playing
          config={playerConfig}
          onError={(e) => console.error("Video player error:", e)}
        />
        {!is_placement && (
          <WatchInNewTabButton
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
          >
            Watch in new tab
          </WatchInNewTabButton>
        )}
        {isAdmin && (
          <DownloadButton $solo={is_placement}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading
              ? downloadStatus === "preparing"
                ? "Preparing..."
                : downloadStatus === "processing"
                  ? "Processing..."
                  : downloadStatus === "ready"
                    ? "Downloading..."
                    : "Downloading..."
              : "Download"}
          </DownloadButton>
        )}
      </PlayerWrapper>
    </>
  );
};

export default memo(PreviewWithPlayer);

const PlayerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  min-height: 307px;

  @media (max-width: 560px) {
    min-height: 100%;
  }
`;

const WatchInNewTabButton = styled(Button)`
  position: absolute;
  bottom: -35px;
  right: 1px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  z-index: 10;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    border-color: rgba(255, 255, 255, 0.5);
  }

  .MuiButton-startIcon {
    margin-right: 4px;
  }

  @media (max-width: 560px) {
    bottom: -66px;
    right: 5px;
    padding: 4px 8px;
    font-size: 11px;
  }
`;

const DownloadButton = styled(Button)<{ $solo?: boolean }>`
  position: absolute;
  bottom: -35px;
  right: ${(p) => (p.$solo ? "1px" : "150px")};
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  z-index: 10;
  white-space: nowrap;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    color: white;
  }

  .MuiButton-startIcon {
    margin-right: 4px;
  }

  @media (max-width: 560px) {
    bottom: -66px;
    right: ${(p) => (p.$solo ? "5px" : "120px")};
    padding: 4px 8px;
    font-size: 11px;
  }
`;
